// notificationSlice — Redux slice for in-app notification state.
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '~/store';
import { createAppAsyncThunk } from '~/utils/redux';
import { notificationApi, type Notification, type NotificationCommentReaction } from '../api';

function shouldIncludeNotification(notification: Notification): boolean {
  // Mobile-only events share the user socket but must not enter the web inbox or toast stream.
  return notification.in_app_enabled !== false;
}

// ---------- State ----------

export interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  status: 'idle' | 'loading' | 'error';
  // [why] Separate status for load-more so the initial fetch error doesn't
  // block the "Load more" button, and load-more errors can be surfaced independently.
  loadMoreStatus: 'idle' | 'loading' | 'error';
  hasMore: boolean;
  cursor: string | null;
  // [why] Track in-flight deletes so the UI can show a spinner instead of
  // the × button while waiting for the API, and roll back on failure.
  deletingIds: string[];
  // [why] Generation counter incremented on "Clear All" so stale in-flight
  // fetch results (dispatched before the clear) are discarded instead of
  // re-populating the list.
  fetchGeneration: number;
  // [why] Track clear-all errors separately so the panel can show an inline
  // error message when the bulk delete fails — without a toast that
  // disappears when the panel is closed.
  clearAllStatus: 'idle' | 'loading' | 'error';
  // [why] Timestamp of the last successful "Clear All". Used by addNotification
  // to discard WebSocket events for notifications created before the clear.
  lastClearTimestamp: number;
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  status: 'idle',
  loadMoreStatus: 'idle',
  hasMore: false,
  cursor: null,
  deletingIds: [],
  fetchGeneration: 0,
  clearAllStatus: 'idle',
  lastClearTimestamp: 0,
};

// ---------- Thunks ----------

export const fetchNotificationsThunk = createAppAsyncThunk(
  'notifications/fetch',
  async (_, { getState, rejectWithValue }) => {
    // [why] Capture the generation at dispatch time so the fulfilled handler
    // can discard stale results if a "Clear All" happened while in-flight.
    const generation = getState().notifications.fetchGeneration;
    try {
      const result = await notificationApi.list({ limit: 20 });
      return { ...result, _generation: generation };
    } catch {
      return rejectWithValue('fetch-failed');
    }
  }
);

export const fetchMoreNotificationsThunk = createAppAsyncThunk(
  'notifications/fetchMore',
  async (_, { getState, rejectWithValue }) => {
    const state = getState().notifications;
    const cursor = state.cursor;
    // [why] If cursor is null we can't fetch the next page — the API would
    // return page 1 again, the fulfilled handler would filter out all
    // already-known IDs, and the button would silently do nothing forever.
    if (!cursor) {
      return rejectWithValue('no-cursor');
    }
    const generation = state.fetchGeneration;
    try {
      const result = await notificationApi.list({ limit: 20, cursor });
      return { ...result, _generation: generation };
    } catch {
      return rejectWithValue('fetch-more-failed');
    }
  }
);

export const markReadThunk = createAppAsyncThunk(
  'notifications/markRead',
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      await notificationApi.markRead({ id });
      return id;
    } catch {
      return rejectWithValue('mark-read-failed');
    }
  }
);

export const markAllReadThunk = createAppAsyncThunk(
  'notifications/markAllRead',
  async (_, { rejectWithValue }) => {
    try {
      return await notificationApi.markAllRead();
    } catch {
      return rejectWithValue('mark-all-read-failed');
    }
  }
);

export const deleteNotificationThunk = createAppAsyncThunk(
  'notifications/delete',
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      await notificationApi.deleteOne({ id });
      return id;
    } catch {
      return rejectWithValue('delete-failed');
    }
  }
);

export const clearAllNotificationsThunk = createAppAsyncThunk(
  'notifications/clearAll',
  async (_, { rejectWithValue }) => {
    try {
      await notificationApi.deleteAll();
    } catch {
      return rejectWithValue('clear-all-failed');
    }
  }
);

export const markUnreadThunk = createAppAsyncThunk(
  'notifications/markUnread',
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      await notificationApi.markUnread({ id });
      return id;
    } catch {
      return rejectWithValue('mark-unread-failed');
    }
  }
);

// ---------- Slice ----------

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // Called by useNotificationSync when a WS notification_created event arrives
    addNotification(state, action: PayloadAction<Notification>) {
      // [why] If a "Clear All" was dispatched, discard WS events for
      // notifications created before the clear — prevents cleared
      // notifications from re-appearing in the UI via WebSocket.
      if (
        state.lastClearTimestamp > 0 &&
        action.payload.created_at &&
        new Date(action.payload.created_at).getTime() <= state.lastClearTimestamp
      ) {
        return;
      }

      if (!shouldIncludeNotification(action.payload)) {
        return;
      }

      const existingIndex = state.notifications.findIndex((n) => n.id === action.payload.id);
      if (existingIndex === -1) {
        state.notifications.unshift(action.payload);
        if (!action.payload.read) {
          state.unreadCount += 1;
        }
        return;
      }

      const existing = state.notifications[existingIndex];
      if (!existing) return;

      const nextNotification = {
        ...existing,
        ...action.payload,
        // Keep already-hydrated fields when realtime payload omits them.
        ...((action.payload.comment_content ?? existing.comment_content) !== undefined
          ? { comment_content: action.payload.comment_content ?? existing.comment_content }
          : {}),
        ...((action.payload.comment_reactions ?? existing.comment_reactions) !== undefined
          ? { comment_reactions: action.payload.comment_reactions ?? existing.comment_reactions }
          : {}),
      } satisfies Notification;

      state.notifications.splice(existingIndex, 1);
      state.notifications.unshift(nextNotification);

      if (existing.read && !nextNotification.read) {
        state.unreadCount += 1;
      } else if (!existing.read && nextNotification.read) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },

    // Keeps comment reactions in Redux so close/reopen preserves optimistic updates.
    setNotificationCommentReactions(
      state,
      action: PayloadAction<{ id: string; reactions: NotificationCommentReaction[] }>
    ) {
      const notification = state.notifications.find((item) => item.id === action.payload.id);
      if (!notification) return;
      notification.comment_reactions = action.payload.reactions;
    },

    setNotificationReadState(state, action: PayloadAction<{ id: string; read: boolean }>) {
      const notification = state.notifications.find((item) => item.id === action.payload.id);
      if (!notification || notification.read === action.payload.read) return;

      notification.read = action.payload.read;
      if (action.payload.read) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      } else {
        state.unreadCount += 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationsThunk.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchNotificationsThunk.fulfilled, (state, action) => {
        // [why] If a "Clear All" was dispatched while this fetch was in-flight,
        // discard the stale results so cleared notifications don't re-appear.
        if (action.payload._generation !== state.fetchGeneration) {
          return;
        }
        state.status = 'idle';
        // [why] Reset clear-all error on successful fetch — the server data
        // is now accurate, so any stale error state should be cleared.
        state.clearAllStatus = 'idle';
        const filteredNotifications = action.payload.data.filter(shouldIncludeNotification);
        const existingIds = new Set(state.notifications.map((n) => n.id));
        const newOnes = filteredNotifications.filter((n) => !existingIds.has(n.id));
        // [why] Merge instead of replace — polling refreshes must not wipe
        // pages accumulated via "Load more". Prepend new items, keep existing.
        state.notifications = [...newOnes, ...state.notifications];
        state.unreadCount = state.notifications.filter((n) => !n.read).length;
        // [why] Only seed cursor/hasMore on initial load. Overwriting them
        // after load-more resets pagination state and causes the next
        // "Load more" to re-fetch already-loaded pages (silent no-op).
        const isInitialLoad = state.cursor === null;
        if (isInitialLoad) {
          state.hasMore = action.payload.metadata.hasMore;
          state.cursor = action.payload.metadata.cursor;
        }
      })
      .addCase(fetchNotificationsThunk.rejected, (state) => {
        state.status = 'error';
      })
      .addCase(fetchMoreNotificationsThunk.pending, (state) => {
        state.loadMoreStatus = 'loading';
      })
      .addCase(fetchMoreNotificationsThunk.fulfilled, (state, action) => {
        // [why] If a "Clear All" was dispatched while this fetch was in-flight,
        // discard the stale results so cleared notifications don't re-appear.
        if (action.payload._generation !== state.fetchGeneration) {
          return;
        }
        state.loadMoreStatus = 'idle';
        const existingIds = new Set(state.notifications.map((n) => n.id));
        const newOnes = action.payload.data
          .filter(shouldIncludeNotification)
          .filter((n) => !existingIds.has(n.id));
        state.notifications.push(...newOnes);
        state.unreadCount = state.notifications.filter((n) => !n.read).length;
        // [why] If the server returned no new items (all were already in
        // state), the cursor may be stale. Stop showing the button to
        // prevent an infinite silent-no-op loop.
        if (newOnes.length === 0) {
          state.hasMore = false;
          state.cursor = null;
          return;
        }
        state.hasMore = action.payload.metadata.hasMore;
        state.cursor = action.payload.metadata.cursor;
      })
      .addCase(fetchMoreNotificationsThunk.rejected, (state, action) => {
        state.loadMoreStatus = 'error';
        // [why] If cursor was null the API can't fetch the next page.
        // Hide the button instead of showing a retry-able error.
        if (action.payload === 'no-cursor') {
          state.hasMore = false;
          state.loadMoreStatus = 'idle';
        }
      })
      .addCase(markReadThunk.fulfilled, (state, action) => {
        const n = state.notifications.find((n) => n.id === action.payload);
        if (n && !n.read) {
          n.read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markUnreadThunk.fulfilled, (state, action) => {
        const n = state.notifications.find((n) => n.id === action.payload);
        if (n && n.read) {
          n.read = false;
          state.unreadCount += 1;
        }
      })
      .addCase(markAllReadThunk.fulfilled, (state) => {
        for (const n of state.notifications) {
          n.read = true;
        }
        state.unreadCount = 0;
      })
      .addCase(deleteNotificationThunk.pending, (state, action) => {
        // [why] Track the id so the UI can show a spinner instead of the × button.
        if (!state.deletingIds.includes(action.meta.arg.id)) {
          state.deletingIds.push(action.meta.arg.id);
        }
      })
      .addCase(deleteNotificationThunk.fulfilled, (state, action) => {
        const idx = state.notifications.findIndex((n) => n.id === action.payload);
        if (idx !== -1) {
          const item = state.notifications[idx];
          const wasUnread = item ? !item.read : false;
          state.notifications.splice(idx, 1);
          if (wasUnread) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        }
        state.deletingIds = state.deletingIds.filter((id) => id !== action.payload);
      })
      .addCase(deleteNotificationThunk.rejected, (state, action) => {
        // [why] Remove from deletingIds so the × button reappears on failure.
        state.deletingIds = state.deletingIds.filter((id) => id !== action.meta.arg.id);
      })
      .addCase(clearAllNotificationsThunk.pending, (state) => {
        // [why] Optimistically clear the list and bump the generation so any
        // in-flight fetch results (polling, focus refresh, etc.) are discarded
        // when they arrive — prevents cleared notifications from re-appearing.
        state.notifications = [];
        state.unreadCount = 0;
        state.hasMore = false;
        state.cursor = null;
        state.fetchGeneration = Date.now();
        state.lastClearTimestamp = Date.now();
        state.clearAllStatus = 'loading';
      })
      .addCase(clearAllNotificationsThunk.fulfilled, (state) => {
        // [why] Server confirmed deletion. Keep the cleared state and bump
        // generation again in case a fetch was dispatched between pending
        // and fulfilled (unlikely but safe).
        state.notifications = [];
        state.unreadCount = 0;
        state.hasMore = false;
        state.cursor = null;
        state.fetchGeneration = Date.now();
        state.lastClearTimestamp = Date.now();
        state.clearAllStatus = 'idle';
      })
      .addCase(clearAllNotificationsThunk.rejected, (state) => {
        // [why] If the server delete failed, bump generation so the next
        // fetch will re-populate the list from the server (which still has
        // the notifications since the DELETE didn't go through).
        // Don't update lastClearTimestamp — the clear didn't succeed.
        state.fetchGeneration = Date.now();
        state.clearAllStatus = 'error';
      });
  },
});

export const notificationSliceActions = notificationSlice.actions;

// ---------- Selectors ----------

export const selectNotifications = (state: RootState) => state.notifications.notifications;
export const selectUnreadCount = (state: RootState) => state.notifications.unreadCount;
export const selectNotificationStatus = (state: RootState) => state.notifications.status;
export const selectNotificationHasMore = (state: RootState) => state.notifications.hasMore;
export const selectNotificationLoadMoreStatus = (state: RootState) =>
  state.notifications.loadMoreStatus;
export const selectDeletingIds = (state: RootState) => state.notifications.deletingIds;
export const selectClearAllStatus = (state: RootState) => state.notifications.clearAllStatus;

export default notificationSlice.reducer;
